import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ActionType } from '../../stores/action-types';
import { DataTable } from 'primereact/datatable';
import { DataView } from 'primereact/dataview';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Toolbar } from 'primereact/toolbar';
import { Tooltip } from 'primereact/tooltip';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { SelectButton } from 'primereact/selectbutton';
import Snackbar from '../../../components/Snackbar/Snackbar';
import { connect } from 'react-redux';
import { getSmses, exportSmses, sendSms, getRoutersWithSimcards, getNodesWithSimcards } from '../../stores/actions/installerActions';
import { getSystemsNodesRoutersShort } from '../../stores/actions/logsActions';
import 'react-quill/dist/quill.snow.css';
import RouterEditDialog from './EditDialogs/RouterEditDialog';
import { headerStyleSuccess, headerStyleFail, emptyHeaderStyle } from '../../helpers/GlobalVariables';
import { exportRowsToCsv, withTimestamp } from '../../helpers/exportCsv';

interface SmsProps {
    smses: any;
    dbRouters: any;
    smsesLoading: boolean;
    systems: any;
    systemsNodesRoutersShort: any;
    routersWithSimcards: any;
    nodesWithSimcards: any;
    mobileView: boolean;
    updateRouterSuccess: boolean;
    updateRouterFail: boolean;
    getSmses: (params: any) => void;
    exportSmses: (params: any) => Promise<any>;
    getSystemsNodesRoutersShort: () => void;
    getRoutersWithSimcards: () => void;
    getNodesWithSimcards: () => void;
    sendSms: (sms: any) => void;
}

const Sms = ({
    smses,
    dbRouters,
    smsesLoading,
    systems,
    systemsNodesRoutersShort,
    routersWithSimcards,
    nodesWithSimcards,
    mobileView,
    updateRouterSuccess,
    updateRouterFail,
    getSmses,
    exportSmses,
    getSystemsNodesRoutersShort,
    getRoutersWithSimcards,
    getNodesWithSimcards,
    sendSms,
}: SmsProps) => {

    const defaultParams = {
        sortBy: 'date',
        sortDirection: 'desc',
        limit: 100,
        offset: 0,
        number: null,
        systemId: null,
        nodeId: null,
        routerId: null,
        dateStart: null,
        dateEnd: null,
        textFuzzy: null,
        direction: null,
    };

    const emptySms = {
        text: null,
        number: null,
        nodeId: null,
        routerId: null
    }
    const dt = useRef(null);
    const closeNotificationTimer = useRef(null);
    const dispatch = useDispatch();

    const [params, setParams] = useState(defaultParams);
    const [sms, setSms] = useState(emptySms);
    const [smsDialog, setSmsDialog] = useState(false);
    const [sending, setSending] = useState(false);
    const [selectedTextOption, setSelectedTextOption] = useState(null);
    const [editRouterSerialNumber, setEditRouterSerialNumber] = useState(null);
    
    const [mobileFilterDialog, setMobileFilterDialog] = useState(false);
    const [pendingParams, setPendingParams] = useState(defaultParams);
    const [exporting, setExporting] = useState(false);
    const exportAbortController = useRef<AbortController | null>(null);

    useEffect(() => () => exportAbortController.current?.abort(), []);

    const selectedRouterForEdit = useMemo(() =>
        dbRouters?.find(r => r.serialNumber === editRouterSerialNumber) ?? null,
    [editRouterSerialNumber, dbRouters]);

    const closeNotification = useCallback(() => {
        dispatch({ type: ActionType.CLOSE_UPDATE_ROUTER_SUCCESS_NOTIFICATION });
        dispatch({ type: ActionType.CLOSE_UPDATE_ROUTER_FAIL_NOTIFICATION });
    }, [dispatch]);

    useEffect(() => {
        closeNotification();
        getSmses(params);
        getSystemsNodesRoutersShort();
        getNodesWithSimcards();
        getRoutersWithSimcards();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (updateRouterFail || updateRouterSuccess) {
            clearTimeout(closeNotificationTimer.current);
            closeNotificationTimer.current = setTimeout(closeNotification, 6000);
        }
        return () => clearTimeout(closeNotificationTimer.current);
    }, [updateRouterFail, updateRouterSuccess, closeNotification]);

    useEffect(() => {
        if (updateRouterSuccess) {
            getRoutersWithSimcards();
            getNodesWithSimcards();
        }
    }, [updateRouterSuccess]);


    useEffect(() => {
        setSending(false);
        setSmsDialog(false);
    }, [smses]);

    const fieldToDbColumn = {
        ID: 'id',
        number: 'number',
        systemName: 'system_name',
        nodeName: 'node_name',
        routerSerialNumber: 'router_serial_number',
        date: 'date',
        text: 'text',
        direction: 'direction'
    };

    const systemOptions = systems?.map(item => ({
        id: item.ID,
        name: item.name
    })).sort((a, b) => (a.name > b.name) ? 1 : -1) ?? [];

    const routerOptions = systemsNodesRoutersShort?.map(item => ({
        id: item.routerId,
        serialNumber: item.routerSerialNumber,
        systemName: item.systemName,
        nodeName: item.nodeName,
        label: [item.systemName, item.nodeName, item.routerSerialNumber].filter(Boolean).join(' / ')
    })).sort((a, b) => (a.routerSerialNumber > b.routerSerialNumber) ? 1 : -1) ?? [];

    const simcardRouterOptions = routersWithSimcards?.map(item => ({
        id: item.id,
        serialNumber: `${item.systemName ?? ''} ${item.serialNumber}`
    })).sort((a, b) => (a.systemName > b.systemName) ? 1 : -1) ?? [];

    const nodeOptions = nodesWithSimcards?.map(item => ({
        id: item.id,
        name: `${item.systemName} ${item.name}`
    })).sort((a, b) => (a.systemName > b.systemName) ? 1 : -1) ?? [];

    const textOptions = [
        { label: 'REPORT', value: '@WiSeNe@cell.report' },
        { label: 'RESTART', value: '@WiSeNe@restart' },
        { label: 'RESTART (UC)', value: 'restart router' },
    ];

    const dbColumnToField = fieldToDbColumn
        ? Object.fromEntries(
            Object.entries(fieldToDbColumn).map(([key, value]) => [value, key])
            )
        : {};

    const dateTemplate = (rowData) => {
        if (rowData.date) {
            return (new Date(rowData.date)).toLocaleString('sv-SE').replace('T', ' ');
        } 
        return null;
    };

    const systemNameTemplate = (rowData) => {
        if (rowData.systemId) {
            const tooltipId = `sms-systemName-${rowData.ID}`;
            return (
                <>
                    <Tooltip target={`#${tooltipId}`} />
                    <span 
                        id={tooltipId}
                        className="systemName-nodeName" 
                        data-pr-tooltip={rowData.systemName}
                        style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {rowData.systemName}
                    </span>
                </>
            );
        } 
        return null;
    };

    const nodeNameTemplate = (rowData) => {
        if (rowData.nodeId) {
            const tooltipId = `sms-nodeName-${rowData.ID}`;
            return (
                <>
                    <Tooltip target={`#${tooltipId}`} />
                    <span 
                        id={tooltipId}
                        className="systemName-nodeName" 
                        data-pr-tooltip={rowData.nodeName}
                        style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {rowData.nodeName}
                    </span>
                </>
            );
        } 
        return null;
    };

    const textTemplate = (rowData) => {
        if (rowData.text) {
            const text = rowData.text;
            const tooltipId = `sms-text-${rowData.ID}`;
            return (
                <>
                    <Tooltip target={`#${tooltipId}`} />
                    <span 
                        id={tooltipId}
                        className="systemName-nodeName" 
                        data-pr-tooltip={text}
                        style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {text}
                    </span>
                </>
            );
        } 
        return null;
    };

    const routerBodyTemplate = (rowData) => {
        if (!rowData.routerSerialNumber) return null;
        return (
            <div className="flex align-items-center gap-2">
                <span>{rowData.routerSerialNumber}</span>
                <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    size="small"
                    onClick={() => setEditRouterSerialNumber(rowData.routerSerialNumber)}
                />
            </div>
        );
    };

    const openNew = () => {
        setSms(emptySms);
        setSmsDialog(true);
    };

    const onPage = (event) => {
        const newParams = {
            ...params,
            limit: event.rows,
            offset: event.first
        };
        setParams(newParams);
        getSmses(newParams);
    };

    const onSort = (event) => {
        const sortByDb = fieldToDbColumn[event.sortField];
        const sortDirection = event.sortOrder === 1 ? 'asc' : 'desc';
        setParams(prev => ({
            ...prev,
            sortBy: sortByDb,
            sortDirection
        }));

        getSmses({
            ...params,
            sortBy: sortByDb,
            sortDirection
        });
    };

    const applyFilters = () => {
        getSmses(params);
    };

    const updateFilter = (field, value) => {
        setParams((prev) => ({ ...prev, [field]: value, offset: 0 }));
    };

    const clearAllFilters = () => {
        setParams(defaultParams);
        getSmses(defaultParams);
    };

    const exportSms = () => {
        const controller = new AbortController();
        exportAbortController.current = controller;
        setExporting(true);
        exportSmses({ ...params, limit: 1000000, offset: 0 }, controller.signal)
            .then((data) => {
                exportRowsToCsv(withTimestamp('sms'), [
                    { header: 'ID', field: 'ID' },
                    { header: 'System', field: 'systemName' },
                    { header: 'Node name', field: 'nodeName' },
                    { header: 'Number', field: 'number' },
                    { header: 'Router', field: 'routerSerialNumber' },
                    { header: 'Date', value: dateTemplate },
                    { header: 'Text', field: 'text' },
                    { header: 'Direction', field: 'direction' },
                ], data?.data ?? []);
            })
            .catch((err) => { if (err?.name !== 'AbortError') console.error(err); })
            .finally(() => {
                setExporting(false);
                exportAbortController.current = null;
            });
    };

    const cancelSmsExport = () => {
        exportAbortController.current?.abort();
    };

    const openMobileFilterDialog = () => {
        setPendingParams(params);
        setMobileFilterDialog(true);
    };

    const applyMobileFilters = () => {
        const newParams = { ...pendingParams, offset: 0 };
        setParams(newParams);
        getSmses(newParams);
        setMobileFilterDialog(false);
    };

    const resetMobileFilters = () => {
        setPendingParams(defaultParams);
    };

    const activeFilterCount = useMemo(() => {
        const fields = ['number', 'systemId', 'nodeId', 'routerId', 'dateStart', 'dateEnd', 'textFuzzy', 'direction'];
        return fields.filter(f => params[f] !== null && params[f] !== undefined && params[f] !== '').length;
    }, [params]);

    const mobileFilterBar = (
        <div className="flex align-items-center gap-2 mb-3">
            <span className="p-input-icon-left flex-1">
                <i className="pi pi-search" />
                <Dropdown
                    value={params.systemId}
                    options={systemOptions}
                    optionLabel="name"
                    optionValue="id"
                    onChange={(e) => {
                        const newParams = { ...params, systemId: e.value, offset: 0 };
                        setParams(newParams);
                        getSmses(newParams);
                    }}
                    placeholder="Search system..."
                    showClear
                    filter
                    filterBy="name"
                    className="w-full"
                />
            </span>
            <div className="relative">
                <Button
                    icon="pi pi-filter"
                    rounded
                    outlined={activeFilterCount === 0}
                    severity={activeFilterCount > 0 ? 'info' : undefined}
                    onClick={openMobileFilterDialog}
                />
                {activeFilterCount > 0 && (
                    <span
                        className="absolute top-0 right-0 bg-primary text-white border-circle flex align-items-center justify-content-center"
                        style={{ width: '1.2rem', height: '1.2rem', fontSize: '0.65rem', transform: 'translate(30%, -30%)' }}
                    >
                        {activeFilterCount}
                    </span>
                )}
            </div>
            <Button
                icon="pi pi-send"
                severity="success"
                rounded
                onClick={openNew}
                disabled={smsesLoading}
            />
        </div>
    );


    const phoneNumberFilter = () => (
        <InputText
            value={params['number'] || ''}
            onChange={(e) => updateFilter('number', e.target.value || null)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            placeholder={''}
            className="w-full"
        />
    );
    const textFilter = () => (
        <InputText
            value={params['textFuzzy'] || ''}
            onChange={(e) => updateFilter('textFuzzy', e.target.value || null)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            placeholder={''}
            className="w-full"
        />
    );

    const dropdownFilter = (field, options, optionLabel, filterBy = optionLabel) => (
        <Dropdown
            value={params[field]}
            options={options}
            optionLabel={optionLabel}
            optionValue="id"
            onChange={(e) => updateFilter(field, e.value)}
            placeholder="Any"
            showClear
            filter
            filterBy={filterBy}
            className="w-full"
        />
    );

    const onTextOptionChange = (value) => {
        setSelectedTextOption(value);
        // ustaw tekst w sms lub wyczyść jeśli odznaczono
        onInputChange({ target: { value: value ?? '' } }, 'text');
        };

    const handleDateChange = (field: 'dateStart' | 'dateEnd', date: Date | null) => {
        if (!date) {
            updateFilter(field, null);
            return;
        }

        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        updateFilter(field, utcDate.toISOString());
    };

    const dateFilter = (field, id?: string) => (
        <Calendar
            id={id}
            value={params[field] ? new Date(params[field]) : null}
            onChange={(e) => handleDateChange(field, e.value)}
            placeholder="yyyy-mm-dd"
            dateFormat="yy-mm-dd"
            mask="9999-99-99"
            showButtonBar
        />
    );

    const directionFilter = (
        <Dropdown
            value={params.direction}
            options={[
                { label: 'Sent', value: 'sent' },
                { label: 'Received', value: 'received' },
            ]}
            onChange={(e) => updateFilter('direction', e.value)}
            placeholder="Any"
            showClear
            className="w-full"
        />
    );


    const onInputChange = (e, name) => {
        let val = e.value !== undefined ? e.value : e.target?.value ?? null;
        if (name == 'number') {
            val = val.replace(/[^\d+]/g, '');
        }
        setSms(prev => ({ ...prev, [name]: val }));
    };

    const submitSendSms = () => {
        if(!sms.number && !sms.nodeId && !sms.routerId) {
            return;
        }
        let _sms = {...sms};
        if (sms.nodeId) {
            const node = nodesWithSimcards.find(item => item.id === _sms.nodeId);
            _sms.number = node.simcardTelephone;
        } else if (sms.routerId) {
            const router = routersWithSimcards.find(item => item.id === _sms.routerId);
            _sms.number = router.simcardTelephone;
        }
        setSending(true);
        sendSms(_sms);
    };

    const hideDialog = () => {
        setSms(emptySms);
        setSmsDialog(false);
        setSending(false);
    };

    const smsDialogFooter = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={hideDialog} disabled={sending} />
            <Button
                label="Send"
                icon="pi pi-check"
                onClick={submitSendSms}
                loading={sending}
                disabled={sending}
            />
        </React.Fragment>
    );

    const mobileFilterDialogFooter = (
        <div className="flex justify-content-between">
            <Button label="Reset" icon="pi pi-times" text severity="secondary" onClick={resetMobileFilters} />
            <div className="flex gap-2">
                <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setMobileFilterDialog(false)} />
                <Button label="Apply" icon="pi pi-check" onClick={applyMobileFilters} />
            </div>
        </div>
    );

    const cardTemplate = (rowData) => {
        return (
            <div className="col-12">
                {renderMobileNotifications()}
                <div className="border-1 surface-border border-round p-3 mb-2 surface-card">

                    <div className="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 surface-border">
                        <span className="font-bold text-lg">{rowData.ID}</span>
                    </div>

                    <div className="mb-2 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">System</div>
                                <div className="font-medium">{rowData.systemName || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Node name</div>
                                <div className="font-medium">{rowData.nodeName || '---'}</div>
                            </div>
                        </div>
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Number</div>
                                <div className="font-medium">{rowData.number || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Router</div>
                                <div className="font-medium">{routerBodyTemplate(rowData) || '---'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3">
                        <div className="text-xs text-color-secondary mb-1">Text</div>
                        <div className="font-medium">{rowData.text}</div>
                    </div>

                    <div className="flex justify-content-between align-items-center pt-2 border-top-1 surface-border">
                        <div className="flex flex-column">
                            <span className="text-xs text-color-secondary">Date</span>
                            <span className="font-medium">{dateTemplate(rowData)}</span>
                        </div>
                        <Tag
                            value={rowData.direction}
                            className={`px-2 py-1 rounded ${rowData.direction === 'sent' ? 'bg-purple-500 text-white' : 'bg-pink-500 text-white'}`}
                        />
                    </div>

                </div>
            </div>
        );
    };

    const loadingIndicator = (
        <div className="flex items-center">
            <i className="pi pi-spin pi-spinner text-purple-500 text-2xl" />
            <span className="ml-2 text-gray-600">Loading data…</span>
        </div>
    );
    const mobileLoadingIndicator = (
        <div className="flex align-items-center gap-2">
            <i className="pi pi-spin pi-spinner text-purple-500" />
            <span className="text-color-secondary text-sm">Loading data…</span>
        </div>
    );

    const loadingSpacer = <div style={{ visibility: 'hidden' }}>{loadingIndicator}</div>;
    const mobileLoadingSpacer = <div style={{ visibility: 'hidden' }}>{mobileLoadingIndicator}</div>;

    const centerToolbarTemplate = () => {
        return (
            <div className="flex items-center" style={{ width: "200px" }}>
                {smsesLoading ? loadingIndicator : null}
            </div>
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button label="Send SMS" icon="pi pi-plus" severity="success" onClick={openNew} disabled={smsesLoading} />
            </div>
        );
    };

    const renderMobileNotifications = (): JSX.Element => {
        if (!mobileView) return null;
        const failed = updateRouterFail;
        const succeeded = updateRouterSuccess;
        if (failed || succeeded) {
            const headerStyle = failed ? headerStyleFail : headerStyleSuccess;
            const message = failed
                ? 'Saving router failed.'
                : 'Router was saved successfully.';
            return (
                <h5
                    className={updateRouterFail ? 'notification-open-bind-device-fail' : 'notification-open-bind-device-success'}
                    style={headerStyle}
                >
                    {message}
                </h5>
            );
        }
        return <div style={emptyHeaderStyle}></div>;
    };

    const dataView = useMemo(() => {
        if (!smses || !smses.data) return null;
        return (
            <div>
                {mobileFilterBar}
                {smsesLoading && (
                    <div className="mb-3">
                        {mobileLoadingIndicator}
                    </div>
                )}
                <DataView
                    value={smses.data}
                    itemTemplate={cardTemplate}
                    paginator
                    rows={10}
                    paginatorLeft={smsesLoading ? mobileLoadingSpacer : null}
                    paginatorRight={smsesLoading ? mobileLoadingIndicator : null}
                />
            </div>
        );
    }, [smses, smsesLoading, params, updateRouterFail, updateRouterSuccess]);

    const dataTable = useMemo(() => {
        if (!smses || !smses.data) {
            return null;
        }
        return (
            <div className="card">
                {<Toolbar className="mb-4" left={leftToolbarTemplate} center={centerToolbarTemplate} right={() => (
                    <div className="flex flex-wrap gap-2">
                        <Button label="Apply Filters" icon="pi pi-check" onClick={applyFilters} />
                        <Button label="Clear" icon="pi pi-times" severity="secondary" onClick={clearAllFilters} />
                        <Button label="Export" icon="pi pi-file-export" outlined loading={exporting} onClick={exportSms} />
                        {exporting && (
                            <div className="flex align-items-center gap-2">
                                <small className="text-color-secondary">Exporting…</small>
                                <Button label="Cancel" icon="pi pi-times" text severity="secondary" onClick={cancelSmsExport} />
                            </div>
                        )}
                    </div>
                )}> </Toolbar>}

                <DataTable
                    ref={dt}
                    value={smses?.data}
                    lazy
                    paginator
                    rows={params.limit}
                    totalRecords={smses?.totalRecords ?? 0}
                    first={params.offset}
                    sortField={dbColumnToField[params.sortBy] || 'date'}
                    sortOrder={params.sortDirection === 'asc' ? 1 : -1}
                    onPage={onPage}
                    onSort={onSort}
                    dataKey="ID"
                    stripedRows
                    size="small"
                    filterDisplay="row"
                    resizableColumns
                    columnResizeMode="expand"
                    scrollable
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                    paginatorLeft={smsesLoading ? loadingSpacer : null}
                    paginatorRight={smsesLoading ? loadingIndicator : null}
                >
                <Column exportable={false}></Column>
                <Column                    
                    field="ID" 
                    header="ID"
                    style={{maxWidth: '45px'}}
                    sortable>
                </Column>
                <Column 
                    field="systemName" 
                    header="System"
                    body={systemNameTemplate}
                    style={{ maxWidth: '180px' }}
                    sortable
                    filter
                    showFilterMenu={false}
                    filterElement={dropdownFilter('systemId', systemOptions, 'name')}
                    >
                </Column>
                <Column 
                    field="nodeName"
                    header="Node name"
                    body={nodeNameTemplate}
                    style={{ maxWidth: '180px' }}
                    sortable
                    filter
                    showFilterMenu={false}
                    filterElement={dropdownFilter('nodeId', nodeOptions, 'name')}
                    >
                </Column>
                <Column 
                    field="number" 
                    header="Number"
                    sortable
                    filter
                    style={{ maxWidth: '150px' }}
                    showFilterMenu={false}
                    filterElement={() => phoneNumberFilter()}
                    >
                </Column>
                <Column 
                    field="routerSerialNumber" 
                    header="Router"
                    sortable
                    filter
                    body={routerBodyTemplate}
                    filterPlaceholder=""
                    showFilterMenu={false}
                    filterElement={dropdownFilter('routerId', routerOptions, 'label', 'systemName,nodeName,serialNumber')}
                    >
                </Column>
                <Column 
                    field="date" 
                    header="Date"
                    body={dateTemplate}
                    style={{ maxWidth: '200px' }}
                    filter
                    showFilterMenu={false}
                    filterElement={() => (
                        <div className="flex flex-col gap-1">
                            {dateFilter('dateStart')}-
                            {dateFilter('dateEnd')}
                        </div>
                    )}
                    sortable>               
                </Column>
                <Column 
                    field="text" 
                    header="Text"
                    body={textTemplate}
                    style={{ maxWidth: '180px' }}
                    filter
                    showFilterMenu={false}
                    filterElement={() => textFilter()}
                    >
                </Column>
                <Column 
                    field="direction" 
                    header="Direction"
                    body={(rowData) =>
                         <Tag 
                            value={rowData.direction} 
                            className={`px-2 py-1 rounded ${ rowData.direction == 'sent' ? 'bg-purple-500 text-white' : 'bg-pink-500 text-white'}`} 
                        />
                    }
                    sortable
                    style={{textAlign: 'center'}}
                    filter
                    filterPlaceholder='-'
                    showFilterMenu={false}
                    filterElement={() => directionFilter}
                    >                
                </Column>
                </DataTable>
            </div>
        );
    }, [smses, smsesLoading, params, exporting]);
    return (
        <div>
            {smses && smses.data && ( mobileView ? dataView : dataTable)}
            <Dialog visible={smsDialog} 
                    style={{ width: mobileView ? '100%' : '60rem' }} 
                    breakpoints={{ '960px': '75vw', '641px': '90vw' }} 
                    modal className="p-fluid" footer={smsDialogFooter} onHide={hideDialog} 
                    header={'Send SMS'}
                    >
                <div className="formgrid grid">
                    <div className="field col">
                            <label className="mb-3 font-bold">Node</label>
                            <Dropdown
                                value={sms.nodeId}
                                onChange={(e) => onInputChange(e, 'nodeId')}
                                options={nodeOptions}
                                disabled={!!sms.routerId || !!sms.number}
                                invalid={!sms.nodeId && !sms.routerId && !sms.number}
                                optionLabel="name"
                                optionValue="id"
                                placeholder="Select gateway"
                                className="md:w-28rem"
                                filter
                                showClear
                                filterPlaceholder="Type to search..."
                            />
                        </div>
                    <div className="field col">
                            <label className="mb-3 font-bold">Router</label>
                            <Dropdown
                                value={sms.routerId}
                                onChange={(e) => onInputChange(e, 'routerId')}
                                options={simcardRouterOptions}
                                disabled={!!sms.nodeId || !!sms.number}
                                invalid={!sms.nodeId && !sms.routerId && !sms.number}
                                optionLabel="serialNumber"
                                optionValue="id"
                                placeholder="Select Router"
                                className="md:w-28rem"
                                filter
                                showClear
                                filterPlaceholder="Type to search..."
                            />
                    </div>
                </div>
 
                <div className="formgrid grid">
                    <div className= {mobileView ? "field" :"field col"}>
                        <label className="mb-3 font-bold">Number</label>
                        <InputText 
                            value={sms.number} 
                            maxLength={128} 
                            disabled={!!sms.nodeId || !!sms.routerId}
                            invalid={!sms.nodeId && !sms.routerId && !sms.number}
                            onChange={(e) => onInputChange(e, 'number')}
                        />
                    </div> 
                </div>
                <div className="formgrid grid">
                    <div className= {mobileView ? "field" :"field col"}>
                        <label className="mb-3 font-bold">Text</label>

                        <SelectButton
                            value={selectedTextOption}
                            options={textOptions}
                            onChange={(e) => onTextOptionChange(e.value)}
                            className="mb-2"
                        />

                        <InputTextarea value={sms.text} disabled={!!selectedTextOption} rows={3} maxLength={160} onChange={(e) => onInputChange(e, 'text')}/>
                    </div> 
                </div>
            </Dialog>

            <RouterEditDialog
                key={editRouterSerialNumber ?? 'none'}
                visible={!!editRouterSerialNumber}
                initialRouter={selectedRouterForEdit}
                onHide={() => setEditRouterSerialNumber(null)}
            />

            <Dialog
                visible={mobileFilterDialog}
                onHide={() => setMobileFilterDialog(false)}
                header="Filter & Sort"
                style={{ width: '95vw' }}
                footer={mobileFilterDialogFooter}
                modal
                className="p-fluid"
            >
                <div className="mb-4">
                    <label className="font-bold mb-2 block">Sort by</label>
                    <div className="flex gap-2">
                        <Dropdown
                            value={pendingParams.sortBy}
                            options={[
                                { label: 'ID', value: 'id' },
                                { label: 'Number', value: 'number' },
                                { label: 'System', value: 'system_name' },
                                { label: 'Node name', value: 'node_name' },
                                { label: 'Router', value: 'router_serial_number' },
                                { label: 'Date', value: 'date' },
                                { label: 'Text', value: 'text' },
                                { label: 'Direction', value: 'direction' },
                            ]}
                            onChange={(e) => setPendingParams(prev => ({ ...prev, sortBy: e.value }))}
                            className="flex-1"
                        />
                        <Button
                            icon={pendingParams.sortDirection === 'asc' ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'}
                            outlined
                            onClick={() => setPendingParams(prev => ({
                                ...prev,
                                sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc'
                            }))}
                            tooltip={pendingParams.sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Number</label>
                    <InputText
                        value={pendingParams.number || ''}
                        onChange={(e) => setPendingParams(prev => ({ ...prev, number: e.target.value || null }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Node name</label>
                    <Dropdown
                        value={pendingParams.nodeId}
                        options={nodeOptions}
                        optionLabel="name"
                        optionValue="id"
                        onChange={(e) => setPendingParams(prev => ({ ...prev, nodeId: e.value }))}
                        placeholder="Any"
                        showClear
                        filter
                        filterBy="name"
                        className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Router</label>
                    <Dropdown
                        value={pendingParams.routerId}
                        options={routerOptions}
                        optionLabel="label"
                        optionValue="id"
                        onChange={(e) => setPendingParams(prev => ({ ...prev, routerId: e.value }))}
                        placeholder="Any"
                        showClear
                        filter
                        filterBy="systemName,nodeName,serialNumber"
                        className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Date from</label>
                    <Calendar
                        value={pendingParams.dateStart ? new Date(pendingParams.dateStart) : null}
                        onChange={(e) => {
                            const date = e.value as Date | null;
                            if (!date) {
                                setPendingParams(prev => ({ ...prev, dateStart: null }));
                                return;
                            }
                            const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                            setPendingParams(prev => ({ ...prev, dateStart: utc.toISOString() }));
                        }}
                        placeholder="yyyy-mm-dd"
                        dateFormat="yy-mm-dd"
                        mask="9999-99-99"
                        showButtonBar
                        className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Date to</label>
                    <Calendar
                        value={pendingParams.dateEnd ? new Date(pendingParams.dateEnd) : null}
                        onChange={(e) => {
                            const date = e.value as Date | null;
                            if (!date) {
                                setPendingParams(prev => ({ ...prev, dateEnd: null }));
                                return;
                            }
                            const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                            setPendingParams(prev => ({ ...prev, dateEnd: utc.toISOString() }));
                        }}
                        placeholder="yyyy-mm-dd"
                        dateFormat="yy-mm-dd"
                        mask="9999-99-99"
                        showButtonBar
                        className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Text</label>
                    <InputText
                        value={pendingParams.textFuzzy || ''}
                        onChange={(e) => setPendingParams(prev => ({ ...prev, textFuzzy: e.target.value || null }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Direction</label>
                    <Dropdown
                        value={pendingParams.direction}
                        options={[
                            { label: 'Sent', value: 'sent' },
                            { label: 'Received', value: 'received' },
                        ]}
                        onChange={(e) => setPendingParams(prev => ({ ...prev, direction: e.value }))}
                        placeholder="Any"
                        showClear
                        className="w-full"
                    />
                </div>
            </Dialog>

            {!mobileView ? (
                <Snackbar
                    close
                    closeNotification={closeNotification}
                    color={updateRouterFail ? 'danger' : 'success'}
                    message={updateRouterFail
                        ? 'Saving router failed.'
                        : 'Router was saved successfully.'
                    }
                    open={!mobileView && (updateRouterFail || updateRouterSuccess)}
                    place='tc'
                />
            ) : null}
        </div>
    ) 

}


const mapStateToProps = (state) => {
    return {
        smses: state.dashboard.smses,
        smsesLoading: state.dashboard.smsesLoading,
        systems: state.dashboard.systems,
        systemsNodesRoutersShort: state.dashboard.systemsNodesRoutersShort,
        routersWithSimcards: state.dashboard.routersWithSimcards,
        dbRouters: state.dashboard.routers,
        nodesWithSimcards: state.dashboard.nodesWithSimcards,
        mobileView: state.dashboard.mobileView,
        updateRouterSuccess: state.dashboard.updateRouterSuccess,
        updateRouterFail: state.dashboard.updateRouterFail,
    };
};

const actions = {
    getSmses,
    exportSmses,
    getSystemsNodesRoutersShort,
    getRoutersWithSimcards,
    getNodesWithSimcards,
    sendSms
};

export default connect(mapStateToProps, actions)(Sms);
